import React, { useState, useEffect } from "react";
import { api, useAxiosInterceptor } from "../shared/axiosInstance";
import { useNavigate } from "react-router-dom";
import { postcodeValidator } from "postcode-validator";
import countries from "i18n-iso-countries";

// Register locale
countries.registerLocale(require("i18n-iso-countries/langs/en.json"));

const CompleteProfile = () => {
    const navigate = useNavigate();
    useAxiosInterceptor();
    const [userId, setUserId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [formData, setFormData] = useState({
        postalCode: "",
        country: "",
        linkedinProfile: "",
        careerSatisfaction: "",
    });

    useEffect(() => {
        // 1. Get User ID from LocalStorage (set by Google Callback)
        const storedUserId = localStorage.getItem("userId");
        if (!storedUserId) {
            navigate("/signin");
            return;
        }
        setUserId(storedUserId);
    }, [navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "country") {
            // Only allow letters, max 2 chars
            const filtered = value.replace(/[^A-Za-z]/g, "").toUpperCase();
            setFormData({ ...formData, [name]: filtered });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        // Validation Logic
        const countryCode = formData.country.trim();
        if (!countries.isValid(countryCode)) {
            setError("Please enter a valid two-letter country code (e.g. US, MX, CA).");
            setLoading(false);
            return;
        }
        if (!postcodeValidator(formData.postalCode, countryCode)) {
            setError("Invalid postal code for this country.");
            setLoading(false);
            return;
        }

        try {
            // 2. Update the user profile
            // Note: Adjust the endpoint if your backend uses a different route for updates
            await api.put(`/auth/user/${userId}`, formData);

            const localUser = JSON.parse(localStorage.getItem("user") || "{}");
            localStorage.setItem("user", JSON.stringify({ ...localUser, ...formData }));

            // 3. Success! Go to Assessment
            navigate("/assessment");
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error || "Failed to update profile.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-darkblue text-lightgray">
            <div className="w-full max-w-md bg-bluegray shadow-lg rounded-lg p-6">
                <h1 className="text-2xl font-bold text-center text-orange mb-2">
                    One Last Step!
                </h1>
                <p className="text-center text-sm mb-6 text-gray-300">
                    We just need a few details to match you with the right jobs.
                </p>

                {error && <div className="bg-red text-white p-2 rounded mb-4 text-sm">{error}</div>}

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Country */}
                    <div>
                        <label className="block text-sm font-medium mb-1">Country (e.g. US, MX)</label>
                        <input
                            type="text"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            placeholder="e.g. US"
                            maxLength={2}
                            required
                            className="w-full px-3 py-2 rounded bg-deepblue border border-lightblue focus:ring-orange focus:outline-none"
                        />
                    </div>

                    {/* Postal Code */}
                    <div>
                        <label className="block text-sm font-medium mb-1">Postal Code</label>
                        <input
                            type="text"
                            name="postalCode"
                            value={formData.postalCode}
                            onChange={handleChange}
                            placeholder="e.g. 90210"
                            required
                            className="w-full px-3 py-2 rounded bg-deepblue border border-lightblue focus:ring-orange focus:outline-none"
                        />
                    </div>

                    {/* LinkedIn */}
                    <div>
                        <label className="block text-sm font-medium mb-1">LinkedIn Profile</label>
                        <input
                            type="text"
                            name="linkedinProfile"
                            value={formData.linkedinProfile}
                            onChange={handleChange}
                            placeholder="https://linkedin.com/in/..."
                            className="w-full px-3 py-2 rounded bg-deepblue border border-lightblue focus:ring-orange focus:outline-none"
                        />
                    </div>

                    {/* Satisfaction Score */}
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Career Satisfaction (1-10)
                        </label>
                        <div className="flex flex-wrap gap-2 mt-1">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                                <label key={num} className="cursor-pointer flex items-center space-x-1">
                                    <input
                                        type="radio"
                                        name="careerSatisfaction"
                                        value={num}
                                        checked={formData.careerSatisfaction === num.toString()}
                                        onChange={handleChange}
                                        required
                                        className="form-radio text-orange focus:ring-orange"
                                    />
                                    <span className="text-xs">{num}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-2 px-4 bg-orange text-white font-bold rounded hover:bg-deeporange transition"
                    >
                        {loading ? "Saving..." : "Complete Profile"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CompleteProfile;